import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Pusher from "pusher-js";
import {
  MessageSquare,
  Send,
  Clock,
  User,
  Calendar,
  Search,
  Filter,
  Plus,
  MessageCircle,
  Mail,
  Phone,
  MapPin,
  Star,
} from "lucide-react";
import "../../../styles/modern-design-system.css";
import StartConversationModal from "./StartConversationModal";
import ChatModal from "./ChatModal";

const CRMCommunicationsHub = () => {
  // State Management
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pusherConfig, setPusherConfig] = useState(null);
  const [showStartConversationModal, setShowStartConversationModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [activeChats, setActiveChats] = useState([]);

  // Load chats data
  useEffect(() => {
    const loadChats = async () => {
      setLoading(true);
      try {
        // Load trips first
        const tripsResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/api/trips`,
          {
            credentials: "include",
          }
        );

        if (tripsResponse.ok) {
          const tripsData = await tripsResponse.json();
          const trips = tripsData.data || tripsData.trips || tripsData || [];

          // Load clients
          const clientsResponse = await fetch(
            `${import.meta.env.VITE_API_URL}/api/clients`,
            {
              credentials: "include",
            }
          );

          if (clientsResponse.ok) {
            const clientsData = await clientsResponse.json();
            const clients =
              clientsData.data || clientsData.clients || clientsData || [];

            // Group trips by client
            const clientChatsMap = new Map();

            trips.forEach((trip) => {
              const client = clients.find((c) => c.clientid === trip.clientid);
              const clientId = trip.clientid;
              const clientName = client?.fullname || "Unknown Client";
              const clientEmail = client?.email || "unknown@email.com";

              if (!clientChatsMap.has(clientId)) {
                clientChatsMap.set(clientId, {
                  id: clientId,
                  clientName: clientName,
                  clientEmail: clientEmail,
                  trips: [],
                  lastMessageTime: trip.createdat,
                  unreadCount: 0,
                });
              }

              clientChatsMap.get(clientId).trips.push({
                tripId: trip.tripid,
                tripName: trip.tripname,
                status: trip.status,
                createdAt: trip.createdat,
              });
            });

            // Convert map to array and sort by most recent
            const allChatEntries = Array.from(clientChatsMap.values()).sort(
              (a, b) =>
                new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
            );

            setChats(allChatEntries);

            // Filter for active chats (those with actual messages)
            const activeChatsWithMessages = [];
            
            for (const chat of allChatEntries) {
              try {
                // Check if this chat has messages by trying to load them
                const response = await fetch(
                  `${import.meta.env.VITE_API_URL}/api/chat/history/${chat.trips[0].tripId}?limit=1`,
                  { credentials: "include" }
                );
                
                if (response.ok) {
                  const data = await response.json();
                  const messages = data.data?.messages || [];
                  
                  if (messages.length > 0) {
                    activeChatsWithMessages.push(chat);
                  }
                }
              } catch (error) {
                console.error(`Error checking messages for chat ${chat.id}:`, error);
              }
            }

            setActiveChats(activeChatsWithMessages);
          }
        }
      } catch (error) {
        console.error("Error loading chats:", error);
      } finally {
        setLoading(false);
      }
    };

    loadChats();
    loadPusherConfig();
  }, []);

  // Load Pusher configuration
  const loadPusherConfig = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/chat/config`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPusherConfig(data.data);
      }
    } catch (error) {
      console.error("Error loading Pusher config:", error);
    }
  };


  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('Notification permission:', permission);
      });
    }
  }, []);


  // Filter active chats based on search
  const filteredActiveChats = activeChats.filter(
    (chat) =>
      chat.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.clientEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.trips.some((trip) =>
        trip.tripName.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  // Handle starting a new conversation
  const handleStartConversation = async ({ client, message }) => {
    try {
      // Find the most recent trip for this client
      const clientChat = chats.find(chat => chat.id === client.clientid);
      if (!clientChat || clientChat.trips.length === 0) {
        console.error("No trips found for client");
        return;
      }

      const tripId = clientChat.trips[0].tripId;

      // Send the initial message
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/chat/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            tripId: tripId,
            message: message,
            from: "agent",
            senderId: "agent-user",
            senderName: "Agent",
            senderType: "agent",
          }),
        }
      );

      if (response.ok) {
        // Refresh the active chats list
        window.location.reload(); // Simple refresh for now
        
        console.log("New conversation started successfully");
      } else {
        console.error("Failed to start conversation");
      }
    } catch (error) {
      console.error("Error starting conversation:", error);
    }
  };

  // Handle opening chat modal
  const handleOpenChat = (chat) => {
    setSelectedChat(chat);
    setShowChatModal(true);
  };

  // Handle closing chat modal
  const handleCloseChat = () => {
    setShowChatModal(false);
    setSelectedChat(null);
  };

  // Helper function to get client initials
  const getClientInitials = (fullName) => {
    if (!fullName) return "C";
    const names = fullName.trim().split(" ");
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    return fullName.charAt(0).toUpperCase();
  };

  // Format timestamp for display in chat list
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "No date";
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "Unknown date";
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      if (messageDate.getTime() === today.getTime()) {
        return "Today";
      } else if (messageDate.getTime() === yesterday.getTime()) {
        return "Yesterday";
      } else {
        return date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });
      }
    } catch (error) {
      return "Unknown date";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="mx-auto mb-4 w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
          <p className="text-gray-600">Loading chats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 h-full">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <MessageCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Chats</p>
              <p className="text-2xl font-bold text-gray-900">{activeChats.length}</p>
            </div>
          </div>
        </div>
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <User className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Clients</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set(chats.map((c) => c.clientName)).size}
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Response</p>
              <p className="text-2xl font-bold text-gray-900">2.3h</p>
            </div>
          </div>
        </div>
        <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Week</p>
              <p className="text-2xl font-bold text-gray-900">{activeChats.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Chat List */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Active Conversations</h3>
              <p className="mt-1 text-sm text-gray-600">
                {filteredActiveChats.length} conversations with messages
              </p>
            </div>
            <button
              onClick={() => setShowStartConversationModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Chat
            </button>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          {filteredActiveChats.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              {searchQuery ? (
                <>
                  <Search size={64} className="mx-auto mb-4 text-gray-300" />
                  <p className="mb-2 text-lg font-medium">No conversations found</p>
                  <p className="text-sm">Try adjusting your search terms</p>
                </>
              ) : (
                <>
                  <MessageCircle size={64} className="mx-auto mb-4 text-gray-300" />
                  <p className="mb-2 text-lg font-medium">No active conversations</p>
                  <p className="text-sm mb-6">Start a conversation with your clients to see active chats here</p>
                  <button
                    onClick={() => setShowStartConversationModal(true)}
                    className="flex items-center gap-2 px-6 py-3 mx-auto text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Start New Conversation
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {filteredActiveChats.map((chat) => (
                <motion.div
                  key={chat.id}
                  className="group p-6 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer"
                  onClick={() => handleOpenChat(chat)}
                  whileHover={{ scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Avatar */}
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-700 rounded-xl flex items-center justify-center text-white font-semibold text-lg">
                        {getClientInitials(chat.clientName)}
                      </div>
                      
                      {/* Chat Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-lg font-semibold text-gray-900 truncate">
                            {chat.clientName}
                          </h4>
                          {chat.unreadCount > 0 && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-red-500 rounded-full">
                              {chat.unreadCount}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center gap-1">
                            <Mail size={14} />
                            <span className="truncate">{chat.clientEmail}</span>
                          </div>
                        </div>
                        
                        {chat.trips.length > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <div className="flex items-center gap-1 text-blue-600">
                              <Calendar size={14} />
                              <span className="font-medium">{chat.trips[0].tripName}</span>
                            </div>
                            {chat.trips.length > 1 && (
                              <span className="text-gray-500">
                                +{chat.trips.length - 1} more trip{chat.trips.length > 2 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Timestamp and Action */}
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock size={12} />
                        <span>{formatTimestamp(chat.lastMessageTime)}</span>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex items-center gap-1 text-blue-600 text-sm font-medium">
                          <MessageSquare size={14} />
                          <span>Open Chat</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <StartConversationModal
        isOpen={showStartConversationModal}
        onClose={() => setShowStartConversationModal(false)}
        onStartConversation={handleStartConversation}
      />
      
      <ChatModal
        isOpen={showChatModal}
        onClose={handleCloseChat}
        selectedChat={selectedChat}
        pusherConfig={pusherConfig}
      />
    </div>
  );
};

export default CRMCommunicationsHub;